import React, { useState } from 'react'
import { roundWithLocale } from '@sketch/utils'
import { Popover } from '@sketch/components'
import copy from './copy'
import {
  AttributeList,
  FullCopyAttribute,
  Header,
  SubTitle,
} from '../../../components'
import { BlurPopover } from './BlurPopover'
import { getDirtyAttributes } from '../../../components/Style'
import { Blur as BlurProps, BlurType } from '../../../../../../../inspector'
import { ExpandIcon, HoverContainer } from './Blur.styles'

const formatBlurType = (type: BlurType) => {
  switch (type) {
    case BlurType.Motion:
      return 'Motion Blur'
    case BlurType.Zoom:
      return 'Zoom Blur'
    case BlurType.Background:
      return 'Background Blur'
    default:
      return 'Gaussian Blur'
  }
}

interface BlurPropsWithDirtyValues extends BlurProps {
  originalValues?: BlurProps
}

export const Blur = ({
  type,
  center,
  isEnabled,
  motionAngle,
  radius,
  saturation,
  originalValues,
  gradient,
  isProgressive,
}: BlurPropsWithDirtyValues) => {
  const blurTypeFormatted = formatBlurType(type)
  const [isPopoverOpen, setPopoverOpen] = useState(false)

  if (!isEnabled) {
    return null
  }

  const sectionCopyValue = copy({
    type: blurTypeFormatted,
    radius,
    saturation,
    motionAngle,
    center,
  })

  const onClickOutside = () => {
    setPopoverOpen(false)
  }

  const hasStops = gradient?.stops && gradient.stops.length > 0
  const showPopover = hasStops || isProgressive

  const header = showPopover ? (
    <Popover
      visible={isPopoverOpen}
      placement="left"
      popup={
        <BlurPopover
          type={type}
          center={center}
          isEnabled={isEnabled}
          motionAngle={motionAngle}
          radius={radius}
          saturation={saturation}
          copyValue={sectionCopyValue}
          gradient={gradient}
          onClose={onClickOutside}
        />
      }
      spacing="16px"
      data-testid="blur-popover"
      onClickOutside={(e: React.MouseEvent) => {
        e.stopPropagation()
        onClickOutside()
      }}
    >
      <HoverContainer
        onClick={() => setPopoverOpen(!isPopoverOpen)}
        visible={isPopoverOpen}
      >
        <Header
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'isEnabled',
            valueToDisplay: originalValues?.isEnabled ? 'enabled' : 'disabled',
            originalValues,
            labelToDisplay: 'Blur value',
          })}
        >
          <SubTitle>Blur</SubTitle>
        </Header>
        <ExpandIcon />
      </HoverContainer>
    </Popover>
  ) : (
    <Header
      dirtyAttributes={getDirtyAttributes({
        originalValueKey: 'isEnabled',
        valueToDisplay: originalValues?.isEnabled ? 'enabled' : 'disabled',
        originalValues,
        labelToDisplay: 'Blur value',
      })}
    >
      <SubTitle>Blur</SubTitle>
    </Header>
  )

  return (
    <>
      {header}
      <AttributeList>
        <FullCopyAttribute
          label="Type"
          value={blurTypeFormatted}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'type',
            valueToDisplay: originalValues?.type,
            originalValues,
            labelToDisplay: 'Blur Type',
          })}
        />
        <FullCopyAttribute
          label="Progression"
          value={isProgressive ? 'Progressive' : 'Uniform'}
          dirtyAttributes={getDirtyAttributes({
            originalValueKey: 'isProgressive',
            valueToDisplay: originalValues?.isProgressive
              ? 'Progressive'
              : 'Uniform',
            originalValues,
            labelToDisplay: 'Progression',
          })}
        />
        {radius ? (
          <FullCopyAttribute
            label="Radius"
            copyValue={`${radius}`}
            value={`${roundWithLocale(radius, 2)}`}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'radius',
              valueToDisplay: `${roundWithLocale(
                originalValues?.radius ?? 0,
                2
              )}`,
              originalValues,
            })}
          />
        ) : null}
        {saturation && type === BlurType.Background ? (
          <FullCopyAttribute
            label="Saturation"
            copyValue={`${saturation}`}
            value={`${roundWithLocale(saturation, 2)}%`}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'saturation',
              valueToDisplay: `${roundWithLocale(
                originalValues?.saturation ?? 0,
                2
              )}`,
              originalValues,
            })}
          />
        ) : null}
        {motionAngle && type === BlurType.Motion ? (
          <FullCopyAttribute
            label="Angle"
            copyValue={`${motionAngle}`}
            value={`${roundWithLocale(motionAngle, 2)}°`}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'motionAngle',
              valueToDisplay: `${roundWithLocale(
                originalValues?.motionAngle ?? 0,
                2
              )}°`,
              originalValues,
              labelToDisplay: 'Motion Angle',
            })}
          />
        ) : null}
        {type === BlurType.Zoom &&
        center &&
        ((center.x && center.y) || center.x === 0 || center.y === 0) ? (
          <FullCopyAttribute
            label="Angle"
            copyValue={`(${center.x}, ${center.y})`}
            value={`(${roundWithLocale(center.x, 2)}, ${roundWithLocale(
              center.y,
              2
            )})`}
            dirtyAttributes={getDirtyAttributes({
              originalValueKey: 'center',
              valueToDisplay: `(${roundWithLocale(
                originalValues?.center?.x ?? 0,
                2
              )}, ${roundWithLocale(originalValues?.center?.y ?? 0, 2)})`,
              originalValues,
              labelToDisplay: 'Angle',
            })}
          />
        ) : null}
      </AttributeList>
    </>
  )
}
